import { apiFetch } from "@/apis/http/apiFetch";
import type {
  Firm,
  FirmMember,
  FirmEmailQuota,
  PendingFirmInvitation,
  CreateFirmRequest,
  AddMemberRequest,
  UpdateMemberRoleRequest,
  ConfigureMailboxRequest,
  ConfigureFirmEmailQuotaRequest,
  FirmCurvePreference,
  ConfigureFirmCurvePreferenceRequest,
} from "@/shared/types/firms";

export async function fetchFirms(): Promise<Firm[]> {
  const res = await apiFetch("/api/v1/firms");
  return res.json() as Promise<Firm[]>;
}

export async function createFirm(body: CreateFirmRequest): Promise<Firm> {
  const res = await apiFetch("/api/v1/firms", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<Firm>;
}

export async function fetchFirm(firmId: string): Promise<Firm> {
  const res = await apiFetch(`/api/v1/firms/${firmId}`);
  return res.json() as Promise<Firm>;
}

export async function deleteFirm(firmId: string): Promise<void> {
  await apiFetch(`/api/v1/firms/${firmId}`, { method: "DELETE" });
}

export async function fetchFirmMembers(firmId: string): Promise<FirmMember[]> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/members`);
  return res.json() as Promise<FirmMember[]>;
}

export async function fetchPendingFirmInvitations(
  firmId: string,
): Promise<PendingFirmInvitation[]> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/members/invitations`);
  return res.json() as Promise<PendingFirmInvitation[]>;
}

export async function addFirmMember(
  firmId: string,
  body: AddMemberRequest,
): Promise<void> {
  await apiFetch(`/api/v1/firms/${firmId}/members`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateMemberRole(
  firmId: string,
  entraObjectId: string,
  body: UpdateMemberRoleRequest,
): Promise<void> {
  await apiFetch(`/api/v1/firms/${firmId}/members/${entraObjectId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function removeFirmMember(
  firmId: string,
  entraObjectId: string,
): Promise<void> {
  await apiFetch(`/api/v1/firms/${firmId}/members/${entraObjectId}`, {
    method: "DELETE",
  });
}

export async function resendFirmMemberInvitation(
  firmId: string,
  entraObjectId: string,
): Promise<void> {
  await apiFetch(
    `/api/v1/firms/${firmId}/members/${entraObjectId}/resend-invitation`,
    { method: "POST" },
  );
}

export async function removePendingFirmInvitation(
  firmId: string,
  email: string,
): Promise<void> {
  await apiFetch(
    `/api/v1/firms/${firmId}/members/invitations?email=${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );
}

export async function configureMailbox(
  firmId: string,
  body: ConfigureMailboxRequest,
): Promise<void> {
  await apiFetch(`/api/v1/firms/${firmId}/mailboxes`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function removeMailbox(
  firmId: string,
  email: string,
): Promise<void> {
  await apiFetch(
    `/api/v1/firms/${firmId}/mailboxes?email=${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );
}

export async function fetchFirmEmailQuota(
  firmId: string,
): Promise<FirmEmailQuota> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/email-quota`);
  return res.json() as Promise<FirmEmailQuota>;
}

export async function configureFirmEmailQuota(
  firmId: string,
  body: ConfigureFirmEmailQuotaRequest,
): Promise<FirmEmailQuota> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/email-quota`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<FirmEmailQuota>;
}

export async function fetchFirmCurvePreference(
  firmId: string,
): Promise<FirmCurvePreference> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/curve-preference`);
  return res.json() as Promise<FirmCurvePreference>;
}

export async function configureFirmCurvePreference(
  firmId: string,
  body: ConfigureFirmCurvePreferenceRequest,
): Promise<FirmCurvePreference> {
  const res = await apiFetch(`/api/v1/firms/${firmId}/curve-preference`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return res.json() as Promise<FirmCurvePreference>;
}
